import { type RefObject, useEffect, useRef } from "react";

import { useCanvasComments } from "src/pages/projects/library/canvas/canvas-comment-threads";

/** Returns the comment stack's `onCloseComplete`, which puts focus on `target` when the keyboard closes the file's comments and leaves focus on the page. */
export function useRefocusAfterCommentsClose(
  fileId: string,
  target: RefObject<HTMLElement>,
) {
  const { openTile } = useCanvasComments();
  const areCommentsOpen = openTile?.fileId === fileId;
  // Any tile, so a switch to another card's comments leaves focus to that card.
  const isAnyOpen = useRef(openTile !== null);
  isAnyOpen.current = openTile !== null;
  const stopWatchingPointer = useClosedByPointer(areCommentsOpen);
  return () => {
    const isClosedByPointer = stopWatchingPointer();
    if (isAnyOpen.current || isClosedByPointer) return;
    if (document.activeElement === document.body) target.current?.focus();
  };
}

/** Watches input from open until the returned `stop`, which says whether a click closed the comments or landed during their exit. */
function useClosedByPointer(isOpen: boolean) {
  const isPointer = useRef(false);
  const removeListeners = useRef<(() => void) | null>(null);
  const stop = () => {
    removeListeners.current?.();
    removeListeners.current = null;
    return isPointer.current;
  };
  useEffect(() => {
    if (!isOpen) return;
    removeListeners.current?.();
    isPointer.current = false;
    const onPointerDown = () => {
      isPointer.current = true;
    };
    const onKeyDown = () => {
      isPointer.current = false;
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    removeListeners.current = () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen]);
  useEffect(() => () => removeListeners.current?.(), []);
  return stop;
}
